import React, { forwardRef, useMemo, useRef, useEffect } from "react";
import PropTypes from "prop-types";
import { Table, Pagination, Select, Checkbox } from "components/ui";
import TableRowSkeleton from "./loaders/TableRowSkeleton";
import Loading from "./Loading";
import { useTable, usePagination, useSortBy, useRowSelect } from "react-table";
import DataNotFound from "./DataNotFound";
import { CircularProgress } from "@mui/material";

const { Tr, Th, Td, THead, TBody, Sorter } = Table;

const IndeterminateCheckbox = forwardRef((props, ref) => {
  const {
    indeterminate,
    onChange,
    onCheckBoxChange,
    onIndeterminateCheckBoxChange,
    ...rest
  } = props;

  const defaultRef = useRef();
  const resolvedRef = ref || defaultRef;

  useEffect(() => {
    resolvedRef.current.indeterminate = indeterminate;
  }, [resolvedRef, indeterminate]);

  const handleChange = (e) => {
    onChange(e);
    onCheckBoxChange?.(e);
    onIndeterminateCheckBoxChange?.(e);
  };

  return (
    <Checkbox
      className="mb-0"
      ref={resolvedRef}
      onChange={(_, e) => handleChange(e)}
      {...rest}
    />
  );
});

const DataTable = ({
  skeletonAvatarColumns = [],
  columns = [],
  data = [],
  loading = false,
  pagingData = { total: 0, pageIndex: 1, pageSize: 10 },
  onPaginationChange,
  onSelectChange,
  onSort,
  onCheckBoxChange,
  onIndeterminateCheckBoxChange,
  selectable = false,
  pageSizes = [10, 25, 50, 100],
  skeletonAvatarProps,
  autoResetSelectedRows = true,
}) => {
  const { pageSize, pageIndex, total } = pagingData;

  // Update page sizes options
  const pageSizeOption = useMemo(
    () =>
      pageSizes.map((number) => ({ value: number, label: `${number} / page` })),
    [pageSizes]
  );

  // Handling pagination change
  const handlePaginationChange = (page) => {
    if (!loading) {
      onPaginationChange?.(page);
    }
  };

  const handleSelectChange = (value) => {
    if (!loading) {
      onSelectChange?.(Number(value));
    }
  };

  const handleSort = (column) => {
    if (!loading) {
      const { id, isSortedDesc, toggleSortBy, clearSortBy } = column;
      const sortOrder = isSortedDesc ? "desc" : "asc";
      toggleSortBy(!isSortedDesc);
      onSort?.({ order: sortOrder, key: id }, { id, clearSortBy });
    }
  };

  const { getTableProps, getTableBodyProps, headerGroups, prepareRow, page } =
    useTable(
      {
        columns,
        data,
        manualPagination: true,
        manualSortBy: true,
        autoResetSelectedRows: false,
        autoResetSelectedCell: false,
        autoResetSelectedColumn: false,
        autoResetSelectedRows,
      },
      useSortBy,
      usePagination,
      useRowSelect,
      (hooks) => {
        if (selectable) {
          hooks.visibleColumns.push((columns) => [
            {
              id: "selection",
              Header: (props) => (
                <div>
                  <IndeterminateCheckbox
                    {...props.getToggleAllRowsSelectedProps()}
                    onIndeterminateCheckBoxChange={(e) =>
                      onIndeterminateCheckBoxChange(
                        e.target.checked,
                        props.rows
                      )
                    }
                  />
                </div>
              ),
              Cell: ({ row }) => (
                <div>
                  <IndeterminateCheckbox
                    {...row.getToggleRowSelectedProps()}
                    onCheckBoxChange={(e) =>
                      onCheckBoxChange(e.target.checked, row.original)
                    }
                  />
                </div>
              ),
              sortable: false,
            },
            ...columns,
          ]);
        }
      }
    );

  // Update page number when data length changes
  useEffect(() => {
    if (data.length === 0 && pageIndex !== 1) {
      // If no data and pageIndex is not 1, reset to first page
      onPaginationChange?.(1);
    }
  }, [data, pageIndex, onPaginationChange]);

  return (
    <Loading loading={loading && data.length !== 0} type="cover">
      <div
        style={{
          overflow: "auto",
          height: "70vh",
        }}
      >
        <Table overflow={false} {...getTableProps()}>
          <THead
            style={{
              position: "sticky",
              top: -1,
              left: 0,
              zIndex: 2,
            }}
            className="bg-slate-50"
          >
            {headerGroups.map((headerGroup) => (
              <Tr {...headerGroup.getHeaderGroupProps()}>
                {headerGroup.headers.map((column) => (
                  <Th {...column.getHeaderProps()}>
                    {column.render("Header") &&
                      (column.sortable ? (
                        <div
                          className="cursor-pointer"
                          onClick={() => handleSort(column)}
                        >
                          {column.render("Header")}
                          <span>
                            <Sorter sort={column.isSortedDesc} />
                          </span>
                        </div>
                      ) : (
                        <div>{column.render("Header")}</div>
                      ))}
                  </Th>
                ))}
              </Tr>
            ))}
          </THead>
          {!loading && data.length === 0 && (
            <DataNotFound columns={columns.length} />
          )}
          {loading && data.length === 0 ? (
          <div
          style={{
            position: "fixed", // Keeps it fixed on the screen
            display: "flex",
            top: "60%", // Positions it at the vertical center
            left: "60%", // Positions it at the horizontal center
            justifyContent: "center", // Horizontally centers the loader
            alignItems: "center", // Vertically centers the loader
            transform: "translate(-50%, -50%)", // Ensures the element is centered
            zIndex: 9999, // Makes sure it stays on top of other content
          }}
          
        >
          <CircularProgress  />
        </div>

          ) : (
            <TBody {...getTableBodyProps()}>
              {page.map((row, i) => {
                prepareRow(row);
                return (
                  <Tr {...row.getRowProps()}>
                    {row.cells.map((cell) => {
                      return (
                        <Td {...cell.getCellProps()}>{cell.render("Cell")}</Td>
                      );
                    })}
                  </Tr>
                );
              })}
            </TBody>
          )}
        </Table>
      </div>

      {data.length !== 0 && (
        <div className="flex items-center justify-between mt-4">
          <Pagination
            pageSize={pageSize}
            currentPage={pageIndex}
            total={total}
            onChange={handlePaginationChange}
          />
          <div style={{ minWidth: 130 }}>
            <Select
              size="sm"
              menuPlacement="top"
              isSearchable={false}
              value={pageSizeOption.filter(
                (option) => option.value === pageSize
              )}
              options={pageSizeOption}
              onChange={(option) => handleSelectChange(option.value)}
            />
          </div>
        </div>
      )}
    </Loading>
  );
};

DataTable.propTypes = {
  columns: PropTypes.array,
  data: PropTypes.array,
  loading: PropTypes.bool,
  onCheckBoxChange: PropTypes.func,
  onIndeterminateCheckBoxChange: PropTypes.func,
  onPaginationChange: PropTypes.func,
  onSelectChange: PropTypes.func,
  onSort: PropTypes.func,
  pageSizes: PropTypes.arrayOf(PropTypes.number),
  selectable: PropTypes.bool,
  skeletonAvatarColumns: PropTypes.arrayOf(PropTypes.number),
  skeletonAvatarProps: PropTypes.object,
  pagingData: PropTypes.shape({
    total: PropTypes.number,
    pageIndex: PropTypes.number,
    pageSize: PropTypes.number,
  }),
  autoResetSelectedRows: PropTypes.bool,
};

export default DataTable;
